import { Button, type ButtonProps } from '../Button';

/**
 * Admin-themed button variants that maintain amber color scheme
 * while reusing the base Button component functionality
 */
export interface AdminButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
}

export function AdminButton({ variant = 'primary', className = '', ...props }: AdminButtonProps) {
  // Override variant styles for admin theme
  const adminVariantOverrides = {
    primary: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-200 text-white border-amber-600',
    secondary:
      'bg-amber-100 hover:bg-amber-200 focus:ring-amber-200 text-amber-700 border-amber-100 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 dark:text-amber-400',
    danger: 'bg-red-700 hover:bg-red-800 focus:ring-red-200 text-white border-red-700',
    ghost:
      'bg-transparent hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-transparent',
    outline:
      'bg-transparent hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-600',
  };

  return (
    <Button
      {...props}
      className={`${adminVariantOverrides[variant]} ${className}`}
      // Pass through custom variant to override base styling
      variant={variant as ButtonProps['variant']}
    />
  );
}

// Convenience components for admin
export const AdminPrimaryButton = (props: Omit<AdminButtonProps, 'variant'>) => (
  <AdminButton {...props} variant="primary" />
);

export const AdminSecondaryButton = (props: Omit<AdminButtonProps, 'variant'>) => (
  <AdminButton {...props} variant="secondary" />
);

export const AdminGhostButton = (props: Omit<AdminButtonProps, 'variant'>) => (
  <AdminButton {...props} variant="ghost" />
);

export default AdminButton;
